import React, { useState, useRef, useCallback } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Alert,
  Tabs,
  Tab,
} from "@mui/material"
import CloudUploadIcon from "@mui/icons-material/CloudUpload"
import DeleteIcon from "@mui/icons-material/Delete"
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto"
import ImageIcon from "@mui/icons-material/Image"
import CameraAltIcon from "@mui/icons-material/CameraAlt"
import CloseIcon from "@mui/icons-material/Close"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import FileUploadIcon from "@mui/icons-material/FileUpload"

import { uploadFile, getFileDownloadUrl } from "../../helpers/fileStorage"

const ProductImageManager = ({ images = [], onImagesChange }) => {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState(null)
  const [previewImage, setPreviewImage] = useState(null)
  const [dragActive, setDragActive] = useState(false)

  // Camera state
  const [cameraActive, setCameraActive] = useState(false)
  const [capturedImage, setCapturedImage] = useState(null)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const fileInputRef = useRef(null)

  // Handle drag events
  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }, [])

  // Handle drop
  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0])
    }
  }, [])

  // Handle file input change
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0])
    }
  }

  // Upload file
  const handleFileUpload = async (file) => {
    if (!file.type.startsWith("image/")) {
      setError(t("Please select an image file"))
      return
    }

    setError(null)
    setUploading(true)
    setUploadProgress(0)

    try {
      const result = await uploadFile(file, "product-images", (progress) => {
        setUploadProgress(progress)
      }, { entity_type: "product" })

      // Store the file URL for display
      onImagesChange([...images, result.url])
      setDialogOpen(false)
      setPreviewImage(null)
    } catch (err) {
      console.error("Upload error:", err)
      setError(t("Failed to upload image. Please try again."))
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
        audio: false
      })
      streamRef.current = stream
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        videoRef.current.play()
      }
      setCameraActive(true)
      setError(null)
    } catch (err) {
      console.error("Camera error:", err)
      setError(t("Could not access camera. Please check permissions."))
    }
  }

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setCameraActive(false)
    setCapturedImage(null)
  }

  // Capture photo
  const capturePhoto = () => {
    if (!videoRef.current) return

    const canvas = document.createElement("canvas")
    canvas.width = videoRef.current.videoWidth
    canvas.height = videoRef.current.videoHeight
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0)

    const dataUrl = canvas.toDataURL("image/jpeg", 0.9)
    setCapturedImage(dataUrl)
  }

  // Upload captured photo
  const uploadCapturedPhoto = async () => {
    if (!capturedImage) return

    setUploading(true)
    setUploadProgress(0)

    try {
      // Convert base64 to blob
      const response = await fetch(capturedImage)
      const blob = await response.blob()
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" })

      const result = await uploadFile(file, "product-images", (progress) => {
        setUploadProgress(progress)
      }, { entity_type: "product" })

      // Store the file URL for display
      onImagesChange([...images, result.url])
      stopCamera()
      setDialogOpen(false)
    } catch (err) {
      console.error("Upload error:", err)
      setError(t("Failed to upload photo. Please try again."))
    } finally {
      setUploading(false)
      setUploadProgress(0)
    }
  }

  // Delete image
  const handleDeleteImage = (index) => {
    const newImages = images.filter((_, i) => i !== index)
    onImagesChange(newImages)
  }

  // Close dialog
  const handleCloseDialog = () => {
    stopCamera()
    setDialogOpen(false)
    setPreviewImage(null)
    setError(null)
    setActiveTab(0)
  }

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    if (cameraActive && newValue !== 1) {
      stopCamera()
    }
    setActiveTab(newValue)
    setCapturedImage(null)
    setError(null)
  }

  return (
    <Box>
      {/* Image Grid */}
      {images.length > 0 ? (
        <ImageList cols={4} gap={12} sx={{ mb: 2 }}>
          {images.map((img, index) => (
            <ImageListItem
              key={index}
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                border: "2px solid #e2e8f0",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#3b82f6",
                  "& .MuiImageListItemBar-root": { opacity: 1 }
                },
              }}
            >
              <img
                src={img}
                alt={`Product ${index + 1}`}
                loading="lazy"
                style={{
                  width: "100%",
                  height: 100,
                  objectFit: "cover",
                  cursor: "pointer"
                }}
                onClick={() => window.open(img, "_blank")}
              />
              <ImageListItemBar
                sx={{
                  opacity: 0,
                  transition: "opacity 0.2s",
                  background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
                }}
                actionIcon={
                  <IconButton
                    sx={{ color: "white" }}
                    onClick={() => handleDeleteImage(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                }
              />
            </ImageListItem>
          ))}
        </ImageList>
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 4,
            color: "#94a3b8",
            border: "2px dashed #e2e8f0",
            borderRadius: 2,
            mb: 2,
          }}
        >
          <ImageIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
          <Typography variant="body2">{t("No photos uploaded")}</Typography>
        </Box>
      )}

      {/* Add Photo Button */}
      <Button
        variant="outlined"
        startIcon={<AddAPhotoIcon />}
        onClick={() => setDialogOpen(true)}
        sx={{
          borderColor: "#e2e8f0",
          color: "#64748b",
          textTransform: "none",
          borderRadius: 2,
          "&:hover": { borderColor: "#3b82f6", color: "#3b82f6" },
        }}
      >
        {t("Add Photo")}
      </Button>

      {/* Upload Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #e2e8f0", pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" fontWeight={600} color="#1e293b">
              {t("Add Product Photo")}
            </Typography>
            <IconButton size="small" onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": { textTransform: "none", fontWeight: 500 }
            }}
          >
            <Tab icon={<FileUploadIcon />} label={t("Upload")} iconPosition="start" />
            <Tab icon={<CameraAltIcon />} label={t("Camera")} iconPosition="start" />
          </Tabs>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Upload Tab */}
          {activeTab === 0 && (
            <Box
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: `2px dashed ${dragActive ? "#3b82f6" : "#e2e8f0"}`,
                borderRadius: 2,
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragActive ? "#eff6ff" : "transparent",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "#3b82f6",
                  bgcolor: "#f8fafc",
                },
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                style={{ display: "none" }}
              />

              {uploading ? (
                <Box>
                  <CircularProgress size={40} sx={{ mb: 2 }} />
                  <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
                    {t("Uploading...")} {uploadProgress}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{ borderRadius: 1, height: 6 }}
                  />
                </Box>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 1 }} />
                  <Typography variant="body1" color="#475569" fontWeight={500}>
                    {t("Drag and drop an image here")}
                  </Typography>
                  <Typography variant="body2" color="#94a3b8">
                    {t("or click to browse")}
                  </Typography>
                  <Typography variant="caption" color="#94a3b8" sx={{ mt: 1, display: "block" }}>
                    {t("Supports: JPG, PNG, GIF, WebP")}
                  </Typography>
                </>
              )}
            </Box>
          )}

          {/* Camera Tab */}
          {activeTab === 1 && (
            <Box sx={{ textAlign: "center" }}>
              {!cameraActive && !capturedImage && (
                <Box sx={{ py: 4 }}>
                  <PhotoCameraIcon sx={{ fontSize: 48, color: "#94a3b8", mb: 2 }} />
                  <Typography variant="body2" color="#64748b" sx={{ mb: 2 }}>
                    {t("Use your camera to take a product photo")}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<CameraAltIcon />}
                    onClick={startCamera}
                    sx={{
                      bgcolor: "#3b82f6",
                      textTransform: "none",
                      borderRadius: 2,
                      "&:hover": { bgcolor: "#2563eb" },
                    }}
                  >
                    {t("Start Camera")}
                  </Button>
                </Box>
              )}

              {cameraActive && !capturedImage && (
                <Box>
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 2,
                      overflow: "hidden",
                      mb: 2,
                      bgcolor: "#000",
                    }}
                  >
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      style={{
                        width: "100%",
                        maxHeight: 300,
                        objectFit: "cover",
                        display: "block"
                      }}
                    />
                  </Box>
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button
                      variant="outlined"
                      onClick={stopCamera}
                      sx={{
                        borderColor: "#e2e8f0",
                        color: "#64748b",
                        textTransform: "none",
                        borderRadius: 2,
                      }}
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PhotoCameraIcon />}
                      onClick={capturePhoto}
                      sx={{
                        bgcolor: "#3b82f6",
                        textTransform: "none",
                        borderRadius: 2,
                        "&:hover": { bgcolor: "#2563eb" },
                      }}
                    >
                      {t("Capture")}
                    </Button>
                  </Stack>
                </Box>
              )}

              {capturedImage && (
                <Box>
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      mb: 2,
                      border: "2px solid #e2e8f0",
                    }}
                  >
                    <img
                      src={capturedImage}
                      alt="Captured"
                      style={{
                        width: "100%",
                        maxHeight: 300,
                        objectFit: "contain",
                        display: "block"
                      }}
                    />
                  </Box>

                  {uploading ? (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
                        {t("Uploading...")} {uploadProgress}%
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ borderRadius: 1, height: 6 }}
                      />
                    </Box>
                  ) : (
                    <Stack direction="row" spacing={2} justifyContent="center">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setCapturedImage(null)
                          startCamera()
                        }}
                        sx={{
                          borderColor: "#e2e8f0",
                          color: "#64748b",
                          textTransform: "none",
                          borderRadius: 2,
                        }}
                      >
                        {t("Retake")}
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                        onClick={uploadCapturedPhoto}
                        sx={{
                          bgcolor: "#16a34a",
                          textTransform: "none",
                          borderRadius: 2,
                          "&:hover": { bgcolor: "#15803d" },
                        }}
                      >
                        {t("Use Photo")}
                      </Button>
                    </Stack>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e2e8f0" }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: "#64748b",
              textTransform: "none",
              borderRadius: 2,
            }}
          >
            {t("Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

ProductImageManager.propTypes = {
  images: PropTypes.array,
  onImagesChange: PropTypes.func.isRequired,
}

export default ProductImageManager
